

import { resolvePrimaryImage } from '@/lib/utils/image-identity';
import {
  Timeline as BarChart2,
  Check,
  ExpandMore as ChevronDown,
  Edit as Edit2,
  AccountTree as GitBranch,
  Delete as Trash2,
  Person as User,
  Close as X
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteConfirmationModal } from '../mui-delete-confirmation-modal';

import type { Arc, Character } from '@/lib/types';
import type { ChipProps } from '@mui/material';


import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';

interface ArcCardProps {
  arc: Arc;
  character?: Character;
  novelId: string;
}

export function ArcCard({ arc, character, novelId }: ArcCardProps) {
  const { backend } = useFirebase();
  const { novel } = useNovelRepository();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(arc.name);
  const [description, setDescription] = useState(arc.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { t } = useTranslation();

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'complete':
        return 'success';
      case 'partial':
        return 'warning';
      case 'notWritten':
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'complete':
        return t('arcs.status_complete');
      case 'partial':
        return t('arcs.status_partial');
      case 'notWritten':
        return t('arcs.status_not_written');
      default:
        return t('arcs.status_unknown');
    }
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!backend || !novel.id) return;

    setIsSaving(true);
    try {
      await backend.narrative.updateNarrativeItem(
        novel.ownerId,
        novel,
        'arcs',
        arc.id,
        {
          name: name.trim(),
          description: description.trim(),
        }
      );
      setIsEditing(false);
      toast({
        title: t('arcs.success'),
        description: t('arcs.arc_updated'),
      });
    } catch (error) {
      console.error('Error updating arc:', error);
      toast({
        title: t('arcs.error'),
        description: t('arcs.arc_update_failed'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setName(arc.name);
    setDescription(arc.description || '');
    setIsEditing(false);
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleDelete = async () => {
    if (!backend || !novel.id) return;

    setIsDeleting(true);
    try {
      await backend.narrative.deleteNarrativeItem(
        novel.ownerId,
        novel,
        'arcs',
        arc.id
      );

      toast({
        title: t('arcs.success'),
        description: t('arcs.arc_deleted'),
      });
    } catch (error) {
      console.error('Error deleting arc:', error);
      toast({
        title: t('arcs.error'),
        description: t('arcs.arc_delete_failed'),
        variant: 'destructive',
      });
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Get character profile image if available
  const profileUrl = character ? resolvePrimaryImage(character)?.profile?.url : undefined;

  return (
    <Accordion
      sx={{
        mb: 2,
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}
        sx={{
          '& .MuiAccordionSummary-content': {
            width: '100%',
          }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 1 }}>
          {arc.type === 'character' && (
            <Avatar
              src={profileUrl}
              sx={{ width: 40, height: 40, border: '2px solid', borderColor: 'primary.main' }}
            >
              <User sx={{ fontSize: 20 }} />
            </Avatar>
          )}
          {arc.type === 'plot' && (
            <Avatar
              sx={{ bgcolor: 'secondary.light', color: 'secondary.main', width: 40, height: 40 }}
            >
              <GitBranch sx={{ fontSize: 20 }} />
            </Avatar>
          )}

          <Box sx={{ flex: 1 }}>
            {isEditing ? (
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                fullWidth
                variant="standard"
                sx={{
                  '& .MuiInput-root': { fontWeight: 600, fontSize: '1rem' },
                  mb: 0.5
                }}
                autoFocus
                placeholder={t('arcs.arc_name')}
              />
            ) : (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {arc.name}
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={arc.type === 'plot' ? t('arcs.arc_type_plot') : t('arcs.arc_type_character')}
                size="small"
                variant="outlined"
                icon={arc.type === 'plot' ? <GitBranch sx={{ fontSize: 14 }} /> : <User sx={{ fontSize: 14 }} />}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              <Chip
                label={getStatusLabel(arc.narrativeStatus)}
                size="small"
                color={getStatusColor(arc.narrativeStatus) as ChipProps['color']}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            {isEditing ? (
              <>
                <IconButton
                  size="small"
                  color="success"
                  onClick={handleSave}
                  disabled={isSaving || !name.trim()}
                >
                  <Check sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X sx={{ fontSize: 18 }} />
                </IconButton>
              </>
            ) : (
              <IconButton
                size="small"
                onClick={handleStartEdit}
                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
              >
                <Edit2 sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {!isEditing && (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
              >
                <Trash2 sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2 }} />
        {isEditing ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            placeholder={t('arcs.arc_description')}
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {arc.description || t('arcs.no_description')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {!isEditing && (
            <Button
              component={Link}
              to={`/arcs/${arc.id}`}
              variant="contained"
              size="small"
              startIcon={<BarChart2 sx={{ fontSize: 18 }} />}
            >
              {t('arcs.view_graph')}
            </Button>
          )}
        </Box>
      </AccordionDetails>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemName={arc.name}
        description={t('arcs.delete_arc_desc')}
      />
    </Accordion>
  );
}
